import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useMe } from '@/hooks/useMe'
import { useSystemToolsEmail } from '@/hooks/useSystemToolsEmail'
import { Mail, ArrowLeft, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

interface TestResult {
  success: boolean
  to: string
  from?: string
  subject: string
  result: string
  timestamp: string
}

export default function EmailTestPage() {
  const navigate = useNavigate()
  const { user, isLoading: userLoading } = useMe()
  const { sendTestEmail, isLoading } = useSystemToolsEmail()

  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')
  const [results, setResults] = useState<TestResult[]>([])

  // 權限檢查
  if (!userLoading && user?.role_type !== 'sys_admin') {
    navigate('/')
    return null
  }

  const handleSend = async () => {
    if (!to.trim() || !to.includes('@')) {
      toast.error('請輸入有效的 Email 地址')
      return
    }

    try {
      const result = await sendTestEmail({
        to: to.trim(),
        subject: subject.trim() || undefined,
        content: content.trim() || undefined,
      })

      const testResult: TestResult = {
        success: true,
        to: result.to,
        from: result.from,
        subject: result.subject,
        result: result.result,
        timestamp: new Date().toLocaleString('zh-TW'),
      }

      setResults((prev) => [testResult, ...prev])
      toast.success(`測試信已發送至 ${result.to}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const testResult: TestResult = {
        success: false,
        to: to.trim(),
        subject: subject.trim() || '【系統測試】Email 發送測試',
        result: errorMessage,
        timestamp: new Date().toLocaleString('zh-TW'),
      }

      setResults((prev) => [testResult, ...prev])
      toast.error(errorMessage)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/system-tools')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-8 w-8" />
            Email 發送測試
          </h1>
          <p className="text-muted-foreground mt-1">測試系統 Email 發送功能是否正常運作</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">發送測試信</CardTitle>
            <CardDescription>輸入收件人 Email 發送一封測試信，驗證 Resend 設定是否正常</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                收件人 Email <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="test@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                type="email"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">主旨（選填）</label>
              <Input
                placeholder="預設：【系統測試】Email 發送測試"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">內容（選填）</label>
              <Input
                placeholder="預設：這是一封測試郵件，用於驗證 Email 發送功能是否正常運作。"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            <Button onClick={handleSend} disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              發送測試信
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">發送紀錄</CardTitle>
            <CardDescription>本次 session 的測試結果（重新整理後清除）</CardDescription>
          </CardHeader>
          <CardContent>
            {results.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">尚未發送任何測試信</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg border ${r.success ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950' : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {r.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <Badge variant={r.success ? 'default' : 'destructive'} className="text-xs">
                        {r.success ? '成功' : '失敗'}
                      </Badge>
                      <span className="text-xs text-muted-foreground ml-auto">{r.timestamp}</span>
                    </div>
                    {r.from && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">寄件人：</span>
                        {r.from}
                      </div>
                    )}
                    <div className="text-sm">
                      <span className="text-muted-foreground">收件人：</span>
                      {r.to}
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">結果：</span>
                      {r.result}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
